import Drawer from "common/Drawers/Drawer.tsx";
import Calendar from "common/Calendars/Calendar.tsx";
import styled from "styled-components";
import Flex from "common/Boxes/Flex.tsx";
import Text from "common/Typography/Text.tsx";
import { TimeLockOutlined } from "common/Icons/Icons.tsx";
import TimezoneSelector from "features/bots/BotForm/OperationHours/TimezoneSelector.tsx";
import DisableDays from "features/bots/BotForm/OperationHours/DisableDays.tsx";
import { useMemo, useState } from "react";
import DisableWeekHours from "features/bots/BotForm/OperationHours/DisableWeekHours.tsx";
import DisableToggles from "features/bots/BotForm/OperationHours/DisableToggles.tsx";
import Button from "common/Buttons/Button.tsx";
import CalendarOutlined from "@ant-design/icons/CalendarOutlined";
import { MOBILE_SEARCH } from "common/constants.ts";
import { OperationHoursEvent } from "@shared/types";
import { OperationHoursContext } from "features/bots/BotForm/OperationHours/OperationHours.context.ts";
import Link from "common/Typography/Link.tsx";
import useOperationHoursDrawer from "features/bots/BotForm/OperationHours/hooks/useOperationHoursDrawer.ts";

interface OperationHoursDrawerProps {
  open: boolean;
  closeDrawer: () => void;
  viewOnly?: boolean;
}

const OperationHoursDrawer = ({
  open,
  closeDrawer,
  viewOnly,
}: OperationHoursDrawerProps) => {
  const [myEvents, setMyEvents] = useState<OperationHoursEvent[]>();

  const contextValue = useMemo(
    () => ({
      myEvents,
      setMyEvents,
    }),
    [myEvents]
  );

  return (
    <OperationHoursContext.Provider value={contextValue}>
      <InnerOperationHoursDrawer
        closeDrawer={closeDrawer}
        open={open}
        viewOnly={viewOnly}
      />
    </OperationHoursContext.Provider>
  );
};

const InnerOperationHoursDrawer = ({
  open,
  closeDrawer,
  viewOnly,
}: OperationHoursDrawerProps) => {
  const {
    handleCloseDrawer,
    resetCalendar,
    onApply,
    showCalendar,
    measureRef,
    buttonsHeight,
    confirmModalContextHolder,
    timezone,
    setTimezone,
    defaultTimezone,
    myEvents,
    setMyEvents,
    noExtensionScreen,
    setShowCalendar,
  } = useOperationHoursDrawer({ open, closeDrawer });

  const settings = (
    <div>
      <Description>
        <TimeLockIcon />
        Select the time periods when you don't want the bot to trade.
      </Description>
      <Divider />
      <TimezoneSelector
        timezone={timezone}
        setTimezone={setTimezone}
        defaultTimezone={defaultTimezone}
      />
      <Divider />
      <DisableDays />
      <Divider />
      <DisableWeekHours />
      <Divider />
      <DisableToggles />
      <ResetButton>
        <Link onClick={resetCalendar}>Reset Calendar</Link>
      </ResetButton>
    </div>
  );

  const calendar = (
    <Calendar
      myEvents={myEvents || []}
      setMyEvents={setMyEvents}
      popoverTitle="Disabled Trading Hours"
      timezone={timezone}
    />
  );

  const applyButton = (
    <ApplyButton type="primary" onClick={onApply}>
      Apply
    </ApplyButton>
  );

  const buttons = (
    <ButtonsWrapper ref={measureRef}>
      {noExtensionScreen && (
        <CalendarButton
          $type="secondary"
          onClick={() => setShowCalendar(!showCalendar)}
        >
          {showCalendar ? (
            "Back to settings"
          ) : (
            <>
              <CalendarOutlined style={{ marginRight: 8 }} />
              View Calendar
            </>
          )}
        </CalendarButton>
      )}
      {applyButton}
    </ButtonsWrapper>
  );

  const content = (
    <Content>
      <InnerContent
        $isSmallScreen={noExtensionScreen}
        $buttonsHeight={buttonsHeight}
        $showCalendar={showCalendar}
      >
        <Title>{showCalendar ? "Calendar" : "Operation Hours"}</Title>
        {noExtensionScreen && (
          <div
            style={{
              overflow: "auto",
              display: showCalendar ? "block" : "none",
            }}
          >
            {calendar}
          </div>
        )}
        {!showCalendar && settings}
      </InnerContent>
      {noExtensionScreen ? buttons : applyButton}
    </Content>
  );

  return (
    <>
      <StyledDrawer
        open={open}
        onClose={handleCloseDrawer}
        size={
          noExtensionScreen || viewOnly
            ? NO_EXTENSION_CONTENT_WIDTH
            : CONTENT_WIDTH + EXTENSION_WIDTH
        }
        destroyOnHidden={true}
      >
        {viewOnly ? (
          calendar
        ) : (
          <Flex $alignItems="flex-start" style={{ height: "100%" }}>
            {!noExtensionScreen && (
              <Extension style={{ height: window.innerHeight }}>
                {calendar}
              </Extension>
            )}
            {content}
          </Flex>
        )}
      </StyledDrawer>
      {confirmModalContextHolder}
    </>
  );
};

export default OperationHoursDrawer;

const EXTENSION_WIDTH = 1100;
const CONTENT_WIDTH = 400;
const NO_EXTENSION_CONTENT_WIDTH = 600;

const StyledDrawer = styled(Drawer)`
  .ant-drawer-body {
    padding: 0 !important;
  }
`;
const Content = styled.div`
  padding: 16px;
  width: ${CONTENT_WIDTH}px;
  min-width: ${CONTENT_WIDTH}px;
  height: 100%;
  @media (max-width: 900px) {
    width: ${NO_EXTENSION_CONTENT_WIDTH}px;
    height: 100%;
    display: flex;
    flex-direction: column;
    padding: 0;
  }
  @media (max-width: ${MOBILE_SEARCH}) {
    min-width: 100%;
    width: 100%;
  }
`;
const Extension = styled.div`
  width: ${EXTENSION_WIDTH}px;
  border-right: ${(p) => p.theme.borders.light};
  overflow: hidden;
`;
const Title = styled(Text)`
  font-size: ${(p) => p.theme.typography.fontSizes.L};
  text-align: center;
  margin-bottom: 8px;
`;
const Description = styled(Text).attrs({ type: "medium" })`
  display: flex;
  align-items: flex-start;
  margin-top: 16px;
`;
const TimeLockIcon = styled(TimeLockOutlined)`
  font-size: 20px;
  margin-right: 8px;
`;
const Divider = styled.div`
  width: 100%;
  border-bottom: ${(p) => p.theme.borders.light};
  margin: 24px 0;
`;
const ApplyButton = styled(Button)`
  width: 100%;
  margin-top: 24px;
  @media (max-width: 900px) {
    margin-top: 8px;
  }
`;
const CalendarButton = styled(Button)`
  width: 100%;
`;
const ButtonsWrapper = styled.div`
  border-top: ${(p) => p.theme.borders.light};
  width: 100%;
  padding: 16px;
`;
const InnerContent = styled.div<{
  $isSmallScreen: boolean;
  $buttonsHeight: number;
  $showCalendar: boolean;
}>`
  ${(p) =>
    p.$isSmallScreen &&
    `
  flex: 1;
  display: flex;
  flex-direction: column;
  height: calc(100% - ${p.$buttonsHeight}px);
  padding: ${p.$showCalendar ? "16px 0 0" : "16px"};
  overflow: auto;
  `};
`;
const ResetButton = styled.div`
  margin-top: 20px;
`;
